/**
 * Parses `GET /notifications/event-types/` into the grid's view model.
 *
 * The grid renders whatever the catalogue endpoint returns — there is no
 * hardcoded event list anywhere on this screen, so a template shipped in a
 * later data migration turns up as a new row with its shipped defaults and no
 * override, with nothing to hand-edit here (scope doc §D.3).
 *
 * The endpoint has two response shapes and this parser accepts both:
 *
 *  - grouped (the default): `{ meta, domains: [{ domain, label, subdomains:
 *    [{ events: [...] }] }] }`, each event carrying `defaults` / `effective`
 *    objects;
 *  - flat: `{ meta, events: [...] }` / `{ count, domains, results: [...] }`,
 *    each event carrying `effective_*` scalars.
 *
 * Parsing runs over `unknown` with narrow guards rather than trusting the
 * declared type, so a shape change downgrades one row to its defaults instead
 * of throwing the whole screen away.
 */
import {
  NOTIFICATION_DOMAIN_ORDER,
  type NotificationChannel,
  type NotificationFrequency,
  type NotificationScopeSource,
  type NotificationSettings,
  type NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import {
  FREQUENCY_LABELS,
  FREQUENCY_ORDER,
  SETUP_CHANNELS,
  type EventConfig,
  type MasterSwitches,
  type SetupCatalogue,
  type SetupChoice,
  type SetupDomain,
  type SetupEvent,
} from './types';

/** Cadence assumed when a template arrives without one. */
const FALLBACK_FREQUENCY: NotificationFrequency = 'immediate';

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>;

const asRow = (value: unknown): Row | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Row)
    : null;

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asText = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asFlag = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const asId = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const isChannel = (value: unknown): value is NotificationChannel =>
  typeof value === 'string' && SETUP_CHANNELS.includes(value as NotificationChannel);

const isFrequency = (value: unknown): value is NotificationFrequency =>
  typeof value === 'string' && value in FREQUENCY_LABELS;

/** Known channels only, de-duplicated, in the grid's column order. */
const readChannels = (value: unknown): NotificationChannel[] => {
  const found = asList(value).filter(isChannel);
  return SETUP_CHANNELS.filter((channel) => found.includes(channel));
};

/** Order-stable narrowing to the channels the platform ships this event on. */
const narrowToShipped = (
  channels: NotificationChannel[],
  shipped: NotificationChannel[],
): NotificationChannel[] => shipped.filter((channel) => channels.includes(channel));

/**
 * The channels this tenant has actually chosen — the list the write API speaks.
 *
 * `effective.channels` cannot answer this, and reading it as if it could is what
 * made saves fail. The API shapes that block by *frequency*
 * (`FrequencyChannelPolicy.channels_for`): an event shipping `[bell, email]` on
 * a batched cadence comes back as `[bell, digest]`, because batched mail is
 * delivered on the digest surface. Sending that back is the widening the API
 * refuses with `channel_widening`, since `digest` is not one of the event's
 * shipped channels.
 *
 * The tenant's own `channel_override` is the only record of what an admin
 * ticked, so it is the source here; a row without one has narrowed nothing and
 * therefore holds the full shipped list.
 */
const readTenantChannels = (
  row: Row,
  override: Row | null,
  shipped: NotificationChannel[],
): NotificationChannel[] => {
  const stored = override?.['channel_override'] ?? row['channel_override'];
  return Array.isArray(stored)
    ? narrowToShipped(readChannels(stored), shipped)
    : shipped;
};

/**
 * A cadence, or `null` for "inherit".
 *
 * Anything the API sends that is not one of the five known values is read as
 * inherit rather than guessed at — an unknown cadence must not silently become
 * `immediate` and start emailing on every event.
 */
const readFrequency = (value: unknown): NotificationFrequency | null =>
  isFrequency(value) ? value : null;

/** The tenant-wide cadence, where `''` means "no blanket cadence set". */
const readTenantDefault = (value: unknown): NotificationTenantDefaultFrequency =>
  isFrequency(value) ? value : '';

/** The three ways the backend can have resolved the scope. */
const SCOPE_SOURCES: readonly NotificationScopeSource[] = ['site', 'company', 'home'];

/**
 * How the server said it resolved the company, or `''` when it did not say.
 *
 * An unrecognised value reads as "did not say" rather than being rendered raw:
 * the header would otherwise state a reason nobody wrote.
 */
const readScopeSource = (value: unknown): NotificationScopeSource | '' =>
  SCOPE_SOURCES.find((source) => source === value) ?? '';

/** Title-cases a slug when the API sent no label for it. */
const humanise = (slug: string): string =>
  slug ? slug.replace(/[_.]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'General';

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/**
 * Reads one config block, accepting either the nested object (`defaults` /
 * `effective`) or the flat `*_channels` scalars.
 *
 * `frequency` is passed in rather than read here: cadence lives on the tenant
 * override, not in the defaults/effective blocks, because the grid's question
 * is "does this row have its own cadence?" and only the override answers it.
 */
function readConfig(
  row: Row,
  nestedKey: string,
  flatPrefix: string,
  fallbackChannels: NotificationChannel[],
  frequency: NotificationFrequency | null,
): EventConfig {
  const nested = asRow(row[nestedKey]);
  if (nested) {
    return {
      enabled: asFlag(nested['is_enabled'], true),
      channels: readChannels(nested['channels']),
      frequency,
    };
  }
  return {
    enabled: asFlag(row[`${flatPrefix}is_enabled`], asFlag(row['is_active'], true)),
    channels: readChannels(row[`${flatPrefix}channels`] ?? fallbackChannels),
    frequency,
  };
}

/** One catalogue row -> one grid row. Returns null when it has no usable key. */
export function parseEvent(raw: unknown, domain: string, domainLabel: string): SetupEvent | null {
  const row = asRow(raw);
  if (!row) return null;
  const key = asText(row['key']);
  if (!key) return null;

  const shippedChannels = readChannels(row['available_channels'] ?? row['default_channels']);
  const override = asRow(row['override']);
  // A row runs on a *custom* cadence only when the tenant override actually
  // carries one. An override that merely narrows channels still inherits the
  // tenant default, and must keep tracking it when the default changes.
  const customFrequency = readFrequency(
    override?.['frequency'] ?? row['frequency_override'],
  );
  // The template's shipped cadence — the last of the three resolution levels,
  // reached only when the tenant has set no blanket default.
  const platformFrequency =
    readFrequency(asRow(row['defaults'])?.['frequency'] ?? row['frequency'])
    ?? FALLBACK_FREQUENCY;
  // The shipped *state* is "no override", so reset-to-default puts a row back
  // to inheriting rather than pinning it to whatever it happened to resolve to.
  const defaults = readConfig(row, 'defaults', '', shippedChannels, null);
  // Shipped channels are the ceiling for both the ticks and the write payload.
  const available = shippedChannels.length ? shippedChannels : readChannels(defaults.channels);
  const effective: EventConfig = {
    ...readConfig(row, 'effective', 'effective_', available, customFrequency),
    // Not the frequency-shaped `effective.channels` — see readTenantChannels.
    channels: readTenantChannels(row, override, available),
  };
  const rowDomain = asText(row['domain'], domain);
  const subdomain = asText(row['subdomain']);

  return {
    key,
    name: asText(row['display_name'], key),
    description: asText(row['description']),
    domain: rowDomain,
    domainLabel: asText(row['domain_label'], asText(row['domain_display'], domainLabel || humanise(rowDomain))),
    subdomain,
    subdomainLabel: asText(row['subdomain_label'], asText(row['subdomain_display'], humanise(subdomain))),
    // Shipped channels are the ceiling: the API rejects a widening write, so
    // an unavailable channel renders as a locked, unticked box rather than a
    // checkbox that fails on save.
    availableChannels: available,
    platformFrequency,
    // The reset target is the same ceiling — a "restore defaults" write must
    // never name a channel the ceiling would reject either.
    defaults: { ...defaults, channels: available },
    effective,
    overrideId: override ? asId(override['id']) : null,
    isOverridden: asFlag(row['is_overridden'], override !== null),
    canDisable: asFlag(row['can_disable'], !asFlag(row['is_required'], false)),
    baselineDaily: asFlag(row['baseline_daily'], false),
    grouped: asFlag(row['grouped'], false),
    oneDaily: asFlag(row['one_daily'], false),
    reaches: row['reaches'] === 'customer' || row['reaches'] === 'supplier' ? row['reaches'] : 'both',
    isActive: asFlag(row['is_active'], true),
    moduleEnabled: asFlag(row['module_enabled'], asFlag(row['module_available'], true)),
  };
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

const domainRank = (domain: string): number => {
  const index = NOTIFICATION_DOMAIN_ORDER.indexOf(domain as (typeof NOTIFICATION_DOMAIN_ORDER)[number]);
  return index === -1 ? NOTIFICATION_DOMAIN_ORDER.length : index;
};

/** Grouped shape: `domains[].subdomains[].events[]`. */
function parseGrouped(domains: unknown[]): SetupDomain[] | null {
  const sections: SetupDomain[] = [];
  for (const entry of domains) {
    const row = asRow(entry);
    if (!row || !Array.isArray(row['subdomains'])) return null;
    const domain = asText(row['domain']);
    const label = asText(row['label'], asText(row['domain_display'], humanise(domain)));
    const events: SetupEvent[] = [];
    for (const group of asList(row['subdomains'])) {
      const section = asRow(group);
      if (!section) continue;
      for (const event of asList(section['events'])) {
        const parsed = parseEvent(event, domain, label);
        if (parsed) events.push(parsed);
      }
    }
    sections.push({ domain, label, events });
  }
  return sections;
}

/** Flat shape: one list of events, grouped here by `domain`. */
function parseFlat(events: unknown[]): SetupDomain[] {
  const byDomain: Record<string, SetupDomain> = {};
  for (const entry of events) {
    const parsed = parseEvent(entry, '', '');
    if (!parsed) continue;
    const existing = byDomain[parsed.domain];
    if (existing) {
      existing.events.push(parsed);
    } else {
      byDomain[parsed.domain] = {
        domain: parsed.domain,
        label: parsed.domainLabel,
        events: [parsed],
      };
    }
  }
  return Object.keys(byDomain)
    .map((domain) => byDomain[domain])
    .filter((section): section is SetupDomain => section !== undefined);
}

/**
 * A choice set from the API, relabelled where we have a better word for it.
 *
 * The API decides *which* options exist — nothing here hardcodes a taxonomy, so
 * a cadence shipped later turns up on its own. It does not decide how they are
 * worded: {@link FREQUENCY_LABELS} wins for the values we know, because the
 * wire labels ("Daily digest", "Never email (in-app only)") are written for a
 * help page rather than a column that sits next to a Digest tick.
 *
 * @param value - the raw `meta.*_choices` list.
 * @param labels - preferred labels by value.
 * @param order - fallback ordering when the API sent no choices at all.
 */
function readChoices(
  value: unknown,
  labels: Record<string, string>,
  order: readonly string[],
): SetupChoice[] {
  const parsed = asList(value)
    .map((entry) => asRow(entry))
    .filter((row): row is Row => row !== null)
    .map((row) => {
      const choice = asText(row['value']);
      return { value: choice, label: labels[choice] ?? asText(row['label']) };
    })
    .filter((choice) => choice.value !== '' && choice.label !== '');
  if (parsed.length) return parsed;
  return order.map((value) => ({ value, label: labels[value] ?? value }));
}

/**
 * Parse the catalogue payload into the grid's view model.
 *
 * @param payload - raw `response.data` from `notificationsAPI.getEventTypes`.
 * @returns domains in platform order, each with its events, plus the choice
 *   sets and the server's own `can_configure` verdict.
 */
export function parseCatalogue(payload: unknown): SetupCatalogue {
  const root = asRow(payload);
  const meta = root ? asRow(root['meta']) : null;

  let domains: SetupDomain[] = [];
  if (root) {
    const grouped = Array.isArray(root['domains']) ? parseGrouped(root['domains']) : null;
    if (grouped && grouped.some((section) => section.events.length > 0)) {
      domains = grouped;
    } else if (Array.isArray(root['events'])) {
      domains = parseFlat(root['events']);
    } else if (Array.isArray(root['results'])) {
      domains = parseFlat(root['results']);
    }
  }

  domains = domains
    .filter((section) => section.events.length > 0)
    .sort((a, b) => domainRank(a.domain) - domainRank(b.domain) || a.label.localeCompare(b.label));

  const allEvents = domains.flatMap((section) => section.events);
  return {
    domains,
    eventCount: allEvents.length,
    overriddenCount: allEvents.filter((event) => event.isOverridden).length,
    canConfigure: meta ? asFlag(meta['can_configure'], false) : false,
    frequencyChoices: readChoices(
      meta?.['frequency_choices'] ?? meta?.['tenant_default_frequency_choices'],
      FREQUENCY_LABELS,
      FREQUENCY_ORDER,
    ),
    // The scope block: which company the server actually resolved this
    // catalogue to, and how. The screen states it rather than inferring the
    // company from its own top-bar state, and echoes the id back on every
    // write so a stale view is refused instead of silently landing elsewhere.
    companyId: asId(meta?.['company']),
    companyName: asText(meta?.['company_name']),
    baselineRouting: meta ? asFlag(meta['baseline_routing'], false) : false,
    oneDailyDigest: meta ? asFlag(meta['one_daily_digest'], false) : false,
    scopeSource: readScopeSource(meta?.['company_scope_source']),
  };
}

// ---------------------------------------------------------------------------
// Master switches
// ---------------------------------------------------------------------------

/** `HH:MM:SS` (or `HH:MM`) -> the `HH:MM` an `<input type="time">` wants. */
const asClock = (value: unknown): string => {
  const text = asText(value);
  return /^\d{2}:\d{2}/.test(text) ? text.slice(0, 5) : '';
};

/** Read the master switches out of the settings row. */
export function parseMasterSwitches(settings: NotificationSettings | null): MasterSwitches {
  return {
    emailsEnabled: settings?.emails_enabled ?? true,
    bellEnabled: settings?.bell_enabled ?? true,
    digestEnabled: settings?.digest_enabled ?? true,
    // `''` is a real state, not a missing value: the tenant has declined to set
    // a blanket cadence, so each event uses its own platform default. An
    // unreadable value reads as that same "no blanket cadence" rather than
    // silently claiming the tenant chose one.
    defaultFrequency: readTenantDefault(settings?.default_frequency),
    quietHoursEnabled: settings?.quiet_hours_enabled ?? false,
    quietHoursStart: asClock(settings?.quiet_hours_start),
    quietHoursEnd: asClock(settings?.quiet_hours_end),
    quietHoursApplyToCritical: settings?.quiet_hours_apply_to_critical ?? false,
    defaultMaxEmailsPerDay: settings?.default_max_emails_per_day ?? 0,
    maxEmailsPerUserPerDay: settings?.max_emails_per_user_per_day ?? 0,
    // CR-7281: an unrecognised or missing style reads as never chosen, and
    // anything but an explicit classic reads as the Helix default in force.
    procurementSummaryStyle:
      settings?.procurement_summary_style === 'classic'
      || settings?.procurement_summary_style === 'helix'
        ? settings.procurement_summary_style
        : '',
    effectiveProcurementSummaryStyle:
      settings?.effective_procurement_summary_style === 'classic' ? 'classic' : 'helix',
  };
}

/**
 * The settings endpoint answers `can_configure` alongside the row; it is the
 * same `IsHelixOrCompanyAdmin` verdict the write endpoints enforce, so it is
 * the authority on whether this screen renders read-only.
 */
export function readCanConfigure(payload: unknown, fallback: boolean): boolean {
  const row = asRow(payload);
  return row ? asFlag(row['can_configure'], fallback) : fallback;
}
